import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, SectionHeader } from '@timely/ui';
import { holds } from '@timely/domain';
import { Page } from '../workspace/WorkspaceShell';
import { useActiveWorkspace, useWorkspaces } from '../workspace/WorkspaceProvider';
import { FormNotice } from './AuthLayout';
import { DeleteCompanyDialog, FreshStartDialog } from '../lifecycle/LifecycleDialogs';
import { downloadCompanyExport, exportErrorMessage, formatWhen, type FreshStartResult } from '../lifecycle/lifecycle';

/**
 * Company data: take everything out, and — separately, below a clear line —
 * the two actions that destroy it. Fresh Start clears the book and keeps the
 * company; Delete Company schedules the end of the company itself. Neither is
 * a button beside an everyday Save, and each is shown only to the people the
 * database would let do it.
 */
export function CompanyData() {
    const context = useActiveWorkspace();
    const { reload } = useWorkspaces();
    const navigate = useNavigate();
    const canExport = holds(context, 'workspace.export');
    const canReset = holds(context, 'workspace.reset');
    const isOwner = context.membership.isOwner;

    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState<{ fileName: string; at: string } | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const [dialog, setDialog] = useState<'fresh' | 'delete' | null>(null);
    const [resetResult, setResetResult] = useState<FreshStartResult | null>(null);

    const runExport = async () => {
        setExporting(true);
        setExportError(null);
        try {
            const { fileName } = await downloadCompanyExport(context.workspace.id);
            setExported({ fileName, at: new Date().toISOString() });
        } catch (e) {
            setExportError(exportErrorMessage(e));
        } finally {
            setExporting(false);
        }
    };

    return (
        <Page>
            <SectionHeader title="Company data" subtitle="Take a copy of everything, or start again." />

            {canExport && (
                <Card className="mt-5 px-5 py-5">
                    <h2 className="text-[15.5px] font-bold text-label">Company Data Export</h2>
                    <p className="text-[13.5px] text-label-2 mt-2 leading-relaxed max-w-prose">
                        A ZIP of spreadsheet files with every account, history entry, promise and cheque, the team and
                        its roles, settings, templates and the audit log — all taken at the same moment. It never contains
                        keys, tokens or passwords. It is for portability and recovery assistance: Timely Payment cannot yet
                        restore it automatically.
                    </p>
                    {exportError && <div className="mt-3"><FormNotice tone="error">{exportError}</FormNotice></div>}
                    {exported && (
                        <div className="mt-3"><FormNotice tone="ok">
                            Downloaded {exported.fileName} {formatWhen(exported.at)}.
                        </FormNotice></div>
                    )}
                    <div className="mt-4">
                        <Button variant="primary" onClick={() => { void runExport(); }} disabled={exporting} data-testid="download-export">
                            {exporting ? 'Preparing the export…' : 'Download Company Data Export'}
                        </Button>
                    </div>
                </Card>
            )}

            {(canReset || isOwner) && (
                <section aria-labelledby="danger-zone" className="mt-10 rounded-[16px] border-2 border-dang/40 px-5 py-5 bg-card">
                    <h2 id="danger-zone" className="text-[15.5px] font-bold text-dang">Danger zone</h2>
                    <p className="text-[13px] text-label-3 mt-1">These cannot be undone by Timely Payment. Each asks you to confirm in several ways.</p>

                    {resetResult && (
                        <div className="mt-4"><FormNotice tone="ok">
                            Fresh Start is done: {resetResult.deleted['customers'] ?? 0} accounts, {resetResult.deleted['customer_activity'] ?? 0} history
                            entries and {resetResult.deleted['pdc_cheques'] ?? 0} cheques were removed. Your team, roles and settings are unchanged.
                        </FormNotice></div>
                    )}

                    {canReset && (
                        <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-separator pt-5">
                            <div className="max-w-prose">
                                <h3 className="text-[14px] font-semibold text-label">Fresh Start</h3>
                                <p className="text-[13px] text-label-2 mt-1 leading-relaxed">
                                    Clears the business data — accounts, their history and promises, cheques, import runs and AI report
                                    records. Keeps the company, its people, roles, settings, templates, data source and keys.
                                </p>
                            </div>
                            <Button variant="danger" onClick={() => setDialog('fresh')} data-testid="open-fresh-start">Fresh Start…</Button>
                        </div>
                    )}

                    {isOwner && (
                        <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-separator pt-5">
                            <div className="max-w-prose">
                                <h3 className="text-[14px] font-semibold text-label">Delete this company</h3>
                                <p className="text-[13px] text-label-2 mt-1 leading-relaxed">
                                    Stops the company at once and permanently deletes it, with everything in it, after seven days.
                                    Any owner can cancel before then. Owners only.
                                </p>
                            </div>
                            <Button variant="danger" onClick={() => setDialog('delete')} data-testid="open-delete-company">Delete company…</Button>
                        </div>
                    )}
                </section>
            )}

            {dialog === 'fresh' && (
                <FreshStartDialog
                    context={context}
                    onClose={() => setDialog(null)}
                    onDone={result => { setResetResult(result); setDialog(null); }}
                />
            )}
            {dialog === 'delete' && (
                <DeleteCompanyDialog
                    context={context}
                    onClose={() => setDialog(null)}
                    onScheduled={() => {
                        setDialog(null);
                        // The company is no longer in ordinary use: the shell will
                        // show its pending state, and an owner's way to cancel.
                        void reload().then(() => navigate(`/w/${context.workspace.slug}`, { replace: true }));
                    }}
                />
            )}
        </Page>
    );
}
